import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { aplicarLimitePorTrabajo } from "@/lib/maximos";
import { useBusquedaHeader } from "@/lib/busquedaHeader";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/Panel";
import { Combobox } from "@/components/Combobox";
import { RepuestoPickerDialog } from "@/components/RepuestoPickerDialog";
import { PlantillaRepuestosDialog } from "@/components/PlantillaRepuestosDialog";
import { FotosTrabajo } from "@/components/FotosTrabajo";
import { DatosCuponDialog } from "@/components/DatosCuponDialog";
import { GarantiaReporteClienteDialog } from "@/components/GarantiaReporteClienteDialog";
import { descargarPdf, numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";
import { CheckCircle2, X, Plus, FileText, PackagePlus, PackageCheck, Wrench, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NIVEL_ACEITE = [
  { value: "normal", label: "Normal" },
  { value: "bajo", label: "Bajo Nivel" },
  { value: "alto", label: "Alto Nivel" },
];
const NIVEL_GASOLINA = [
  { value: "lleno", label: "Lleno" },
  { value: "tres_cuartos", label: "3/4" },
  { value: "medio", label: "Medio Tanque" },
  { value: "un_cuarto", label: "1/4" },
  { value: "vacio", label: "Tanque Vacío" },
];
const CON_CITA_OPCIONES = [
  { value: "si", label: "Sí" },
  { value: "no", label: "No" },
];
const PRIORIDADES = [
  { value: "baja", label: "Baja" },
  { value: "normal", label: "Normal" },
  { value: "alta", label: "Alta" },
];

const fmtMiles = (n) => (n === "" || n === null || n === undefined ? "" : Number(n).toLocaleString("es-CR"));
const nuevaTarea = () => ({ key: Math.random().toString(36).slice(2), servicio_id: "", detalle: "", prioridad: "normal", hijos: null, plan: null, repuestos: [], grupos: [], deteccion: null, detalleAnalizado: "", deteccionIgnorada: false, recienAgregada: false, sinGrupoTarifa: false });

const ACCIONES_HIJO = {
  revisar: { label: "Revisar", cls: "bg-slate-200 text-slate-700" },
  limpiar: { label: "Limpiar", cls: "bg-amber-100 text-amber-800" },
  cambiar: { label: "Cambiar", cls: "bg-pink-100 text-pink-700" },
  ajustar: { label: "Ajustar", cls: "bg-teal-100 text-teal-700" },
  lubricar: { label: "Lubricar", cls: "bg-sky-100 text-sky-700" },
  hecho: { label: "Hecho", cls: "bg-blue-100 text-blue-700" },
  na: { label: "N/A", cls: "bg-slate-100 text-slate-400" },
};

export const BadgeAccion = ({ accion, testId }) => {
  const a = ACCIONES_HIJO[accion] || ACCIONES_HIJO.na;
  return (
    <span data-testid={testId}
      className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${a.cls}`}>
      {a.label}
    </span>
  );
};

const GeneraBadge = ({ genera, testId }) => {
  if (!genera || !genera.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 shrink-0">
          <Wrench className="w-2.5 h-2.5" /> + Servicio
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-xs space-y-1 p-3" align="start">
        {genera.map((g) => (
          <p key={g.plantilla_id}>
            Agrega: {g.nombre} ({g.n_repuestos} repuesto{g.n_repuestos === 1 ? "" : "s"})
          </p>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const Campo = ({ label, obligatorio, error, testId, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p data-testid={`${testId}-error`} className="text-xs text-red-500">{error}</p>}
  </div>
);

const Lectura = ({ label, value, testId }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">{label}</label>
    <Input data-testid={testId} value={value || "—"} readOnly disabled className="bg-slate-50 text-slate-600" />
  </div>
);

export default function TrabajoIngresar() {
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const vehiculoPre = useLocation().state?.vehiculoId || "";
  const preAplicado = useRef(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [exito, setExito] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});

  const [vehiculoId, setVehiculoId] = useState("");
  const [medir, setMedir] = useState(true);
  const [km, setKm] = useState("");
  const [aceite, setAceite] = useState("");
  const [gasolina, setGasolina] = useState("");
  const [conCita, setConCita] = useState("");
  const [tareas, setTareas] = useState([nuevaTarea()]);
  const [ultimoTrabajo, setUltimoTrabajo] = useState(null);
  const [clienteFiltroId, setClienteFiltroId] = useState("");
  const [avisoBusqueda, setAvisoBusqueda] = useState(null);
  const [trabajoActivo, setTrabajoActivo] = useState(null);
  const [pendientesAnt, setPendientesAnt] = useState([]);
  const [arrastrados, setArrastrados] = useState([]);
  const [repArrastrados, setRepArrastrados] = useState([]);
  const [cuponSel, setCuponSel] = useState(null);
  const cuponesConsultadosRef = useRef(null);
  const [cuponServicioIds, setCuponServicioIds] = useState(new Set());
  const hondaTrabajoRef = useRef(null);
  const [hondaOrdenEstado, setHondaOrdenEstado] = useState(null); // "preguntar" | "contacto" | null
  const [hondaContactoNombre, setHondaContactoNombre] = useState("");
  const [hondaGuardarContacto, setHondaGuardarContacto] = useState(true);
  const [hondaEnviando, setHondaEnviando] = useState(false);
  const [fotosTrabajoId, setFotosTrabajoId] = useState(null);

  const tareasRef = useRef(tareas);
  const actualizarTareas = (updater) => {
    const next = typeof updater === "function" ? updater(tareasRef.current) : updater;
    tareasRef.current = next;
    setTareas(next);
  };
  const analisisPendientesRef = useRef({});
  const revisandoParaGuardarRef = useRef(false);
  const [avisoSugerencias, setAvisoSugerencias] = useState(null);

  const cargarCatalogos = async () => {
    const [v, c, s] = await Promise.all([api.get("/vehiculos"), api.get("/clientes"), api.get("/servicios", { params: { excluir_solo_cronograma: true } })]);
    setVehiculos(v.data); setClientes(c.data); setServicios(s.data);
  };
  useEffect(() => { cargarCatalogos(); }, []);

  // Solo con permiso honda.ordenes: servicios de cupón de cualquier grupo de cilindrada, para
  // ofrecer la orden Honda tras guardar un trabajo que incluya uno de esos servicios.
  useEffect(() => {
    if (!tienePermiso("honda.ordenes")) return;
    api.get("/grupos-cilindrada").then(({ data }) => {
      const ids = new Set();
      (data || []).forEach((g) => (g.cupones || []).forEach((c) => ids.add(c.servicio_id)));
      setCuponServicioIds(ids);
    }).catch(() => {});
  }, [tienePermiso]);

  const vehiculo = vehiculos.find((v) => v.id === vehiculoId) || null;
  const garantiaServicioIds = useMemo(() => new Set(servicios.filter((s) => s.garantia_honda).map((s) => s.id)), [servicios]);
  const cliente = vehiculo ? clientes.find((c) => c.id === vehiculo.cliente_id) : null;
  const vehiculoMide = !!vehiculo?.medir_kilometraje;
  const kmAnterior = vehiculo ? Number(vehiculo.kilometraje || 0) : 0;
  const kmNum = km === "" ? null : parseInt(km, 10);
  const kmInvalido = vehiculoMide && medir && kmNum !== null && kmNum < kmAnterior;
  const transcurrido = vehiculoMide && medir && kmNum !== null && kmNum >= kmAnterior ? kmNum - kmAnterior : null;

  const seleccionarVehiculo = (id, vehObj) => {
    setVehiculoId(id);
    const v = vehObj || vehiculos.find((x) => x.id === id);
    setMedir(!!v?.medir_kilometraje);
    setKm("");
    setErrores((e) => ({ ...e, vehiculo_id: undefined, kilometraje: undefined }));
    setTrabajoActivo(null);
    setPendientesAnt([]); setArrastrados([]); setRepArrastrados([]);
    cuponesConsultadosRef.current = null; // vehículo distinto: se vuelve a consultar cupones
    setDatosCupon(null);
    setGarantiaReporte(null);
    setConCita("");
    if (id) {
      api.get("/trabajos", { params: { estado: "activo", vehiculo_id: id } })
        .then((r) => setTrabajoActivo(r.data[0] || null))
        .catch(() => {});
      api.get(`/vehiculos/${id}/pendientes-anteriores`)
        .then((r) => setPendientesAnt(r.data))
        .catch(() => {});
      if (!v?.medir_kilometraje) consultarCupones(id, Number(v?.kilometraje || 0));
    }
  };

  const aprobarPendiente = (item) => {
    const sol = item.solicitud;
    setArrastrados((prev) => [...prev, { trabajo_origen_id: item.trabajo_origen_id, solicitud_id: sol.id }]);
    setPendientesAnt((prev) => prev.filter((x) => x.solicitud.id !== sol.id));
    if (sol.tipo === "servicio") {
      actualizarTareas((prev) => {
        if (prev.some((t) => t.servicio_id === sol.ref_id)) return prev;
        const fila = { ...nuevaTarea(), servicio_id: sol.ref_id,
          prioridad: sol.prioridad === "alta" ? "alta" : sol.prioridad === "baja" ? "baja" : "normal" };
        const vacias = prev.filter((t) => !t.servicio_id);
        return vacias.length ? [...prev.filter((t) => t.servicio_id), fila, ...vacias.slice(1)] : [...prev, fila];
      });
    } else {
      setRepArrastrados((prev) => [...prev, item]);
    }
    toast.success("Ítem aprobado; quedará incluido al guardar el trabajo");
  };

  const rechazarPendiente = async (item) => {
    try {
      const { data } = await api.post(`/vehiculos/${vehiculoId}/pendientes-anteriores/rechazar`, {
        trabajo_origen_id: item.trabajo_origen_id, solicitud_id: item.solicitud.id,
      });
      toast.success(data.mensaje);
      setPendientesAnt((prev) => prev.filter((x) => x.solicitud.id !== item.solicitud.id));
    } catch (err) { toast.error(errorMsg(err)); }
  };

  const quitarRepArrastrado = (item) => {
    setRepArrastrados((prev) => prev.filter((x) => x.solicitud.id !== item.solicitud.id));
    setArrastrados((prev) => prev.filter((x) => x.solicitud_id !== item.solicitud.id));
    setPendientesAnt((prev) => [...prev, item]);
  };

  const nombreCli = (c) => [c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(" ");

  const buscarHeader = async (texto) => {
    setAvisoBusqueda({ tipo: "buscando", texto: "Buscando…" });
    const placa = texto.toUpperCase().replace(/\s/g, "");
    try {
      const { data: vehsPlaca } = await api.get("/vehiculos", { params: { placa } });
      const veh = vehsPlaca[0];
      if (veh) {
        setVehiculos((prev) => (prev.some((v) => v.id === veh.id) ? prev : [...prev, veh]));
        setAvisoBusqueda(null);
        seleccionarVehiculo(veh.id, veh);
        setClienteFiltroId(veh.cliente_id || "");
        toast.success(`Vehículo ${veh.placa} seleccionado`);
        return;
      }
      const ced = texto.replace(/\D/g, "");
      const { data: clisCed } = ced ? await api.get("/clientes", { params: { person_id: ced } }) : { data: [] };
      const cli = clisCed[0];
      if (cli) {
        setClientes((prev) => (prev.some((c) => c.id === cli.id) ? prev : [...prev, cli]));
        const { data: vehs } = await api.get("/vehiculos", { params: { cliente_id: cli.id } });
        setVehiculos((prev) => {
          const faltantes = vehs.filter((v) => !prev.some((p) => p.id === v.id));
          return faltantes.length ? [...prev, ...faltantes] : prev;
        });
        setAvisoBusqueda(null);
        if (vehs.length === 0) {
          setClienteFiltroId("");
          seleccionarVehiculo("");
          setAvisoBusqueda({ tipo: "sin_vehiculos", clienteId: cli.id,
            texto: `El cliente ${nombreCli(cli)} no tiene vehículos registrados.` });
          return;
        }
        setClienteFiltroId(cli.id);
        if (vehs.length === 1) {
          seleccionarVehiculo(vehs[0].id, vehs[0]);
          toast.success(`Vehículo ${vehs[0].placa} de ${nombreCli(cli)} seleccionado`);
        } else {
          seleccionarVehiculo("");
          setAvisoBusqueda({ tipo: "elegir",
            texto: `${nombreCli(cli)} tiene ${vehs.length} vehículos. Elija cuál en el selector de "Vehículo".` });
        }
        return;
      }
      setAvisoBusqueda({ tipo: "sin_match", texto: "No se encontró un vehículo con esa placa ni un cliente con esa cédula." });
    } catch {
      setAvisoBusqueda({ tipo: "sin_match", texto: "No se encontró un vehículo con esa placa ni un cliente con esa cédula." });
    }
  };

  useBusquedaHeader("Buscar por placa o número de ID...", false, buscarHeader);

  const clienteFiltro = clienteFiltroId ? clientes.find((c) => c.id === clienteFiltroId) : null;

  const quitarFiltro = () => { setClienteFiltroId(""); setAvisoBusqueda(null); };

  useEffect(() => {
    if (vehiculoPre && !preAplicado.current && vehiculos.some((v) => v.id === vehiculoPre)) {
      preAplicado.current = true;
      seleccionarVehiculo(vehiculoPre);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiculos]);

  const opcionesVehiculo = useMemo(() => {
    if (clienteFiltroId) {
      return vehiculos.filter((v) => v.cliente_id === clienteFiltroId).map((v) => ({
        value: v.id,
        label: `${v.placa} – ${v.modelo?.nombre_mostrar || v.marca}`,
      }));
    }
    return vehiculos.map((v) => ({
      value: v.id,
      label: `${v.placa} – ${v.modelo?.nombre_mostrar || v.marca} – ${v.cliente?.nombre || "sin dueño"}`,
    }));
  }, [vehiculos, clienteFiltroId]);

  const setTarea = (key, campo, valor) =>
    actualizarTareas(tareas.map((t) => (t.key === key ? { ...t, [campo]: valor } : t)));

  const [modalHijos, setModalHijos] = useState([]);
  const [ventana, setVentana] = useState(null); // {tipo: "modal"|"picker"|"plantilla"|"cronoPicker", key, ...}
  const ventanaRef = useRef(null);
  const colaVentanasRef = useRef([]);
  const actualizarVentana = (v) => { ventanaRef.current = v; setVentana(v); };
  // Encola una ventana por-tarea (plan de mantenimiento / repuestos / plantilla / elegir
  // cronograma): si no hay ninguna abierta se muestra de inmediato (igual que antes); si ya hay
  // una abierta (p. ej. aplicando varias sugerencias en lote) se guarda y se muestra en orden,
  // para que cada ventana escriba sus datos en la tarea correcta.
  const encolarVentana = (v) => {
    if (ventanaRef.current) colaVentanasRef.current.push(v);
    else actualizarVentana(v);
  };
  const cerrarVentanaActual = () => actualizarVentana(colaVentanasRef.current.shift() || null);

  const modalKeyActivo = ventana?.tipo === "modal" ? ventana.key : null;
  const pickerKeyActivo = ventana?.tipo === "picker" ? ventana.key : null;
  const plantillaKeyActivo = ventana?.tipo === "plantilla" ? ventana.key : null;
  const cronoPickerActivo = ventana?.tipo === "cronoPicker" ? ventana : null;
  const cuponActivo = ventana?.tipo === "cupon" ? ventana : null;
  const cuponDatosActivo = ventana?.tipo === "cupon_datos" ? ventana : null;
  const garantiaReporteActivo = ventana?.tipo === "garantia_reporte" ? ventana : null;
  const [datosCupon, setDatosCupon] = useState(null);
  const [garantiaReporte, setGarantiaReporte] = useState(null);

  useEffect(() => {
    if (ventana?.tipo === "cupon") setCuponSel(ventana.data.sugerido_servicio_id);
  }, [ventana]);

  // Consulta (UNA sola vez por vehículo) si corresponde ofrecer un cupón de mantenimiento,
  // encolada como cualquier otra ventana para no encimarse con otros diálogos en curso.
  const consultarCupones = async (vehId, kmValue) => {
    if (!vehId || kmValue === null || kmValue === undefined || Number.isNaN(kmValue)) return;
    if (cuponesConsultadosRef.current === vehId) return;
    cuponesConsultadosRef.current = vehId;
    try {
      const { data } = await api.get(`/vehiculos/${vehId}/cupones`, { params: { km: kmValue } });
      if (!data.aplica) return;
      const yaHayCupon = tareasRef.current.some((t) => data.cupones.some((c) => c.servicio_id === t.servicio_id));
      if (yaHayCupon) return;
      encolarVentana({ tipo: "cupon", data });
    } catch { /* no bloquea el ingreso */ }
  };

  const noEsCupon = () => cerrarVentanaActual();

  const agregarCupon = async () => {
    const servicioId = cuponSel;
    const data = cuponActivo?.data;
    cerrarVentanaActual();
    if (!servicioId || tareasRef.current.some((t) => t.servicio_id === servicioId)) return;
    const incompleto = !!data?.cupones?.find((c) => c.servicio_id === servicioId)?.canjeado?.incompleto;
    const vacia = tareasRef.current.find((t) => !t.servicio_id && !(t.detalle || "").trim());
    let key;
    if (vacia) {
      key = vacia.key;
      actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, recienAgregada: true } : t)));
    } else {
      const nueva = { ...nuevaTarea(), recienAgregada: true };
      key = nueva.key;
      actualizarTareas((prev) => [...prev, nueva]);
    }
    await elegirServicio(key, servicioId, { cuponIncompleto: incompleto });
    if (incompleto) {
      const texto = "Se realizan trabajos pendientes del cupón";
      actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, detalle: texto, detalleAnalizado: texto } : t)));
    }
  };

  useEffect(() => {
    if (ventana?.tipo === "modal") setModalHijos((ventana.hijos || []).map((h) => ({ ...h })));
  }, [ventana]);

  // Aplica el resultado de un cronograma a la tarea (setea hijos y abre el modal del cronograma).
  const aplicarCrono = (key, svc, data) => {
    const hijos = data.lineas.map((l) => ({
      hijo_id: l.hijo_id, nombre: l.nombre, accion: l.accion, se_realiza: l.se_realiza, genera: l.genera || [],
      ultimo: l.ultimo || null, proximo_km: l.proximo_km ?? null,
    }));
    actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, hijos, plan: data.cronograma.nombre } : t)));
    if (!svc?.solicita_repuestos) {
      encolarVentana({ tipo: "modal", key, hijos: hijos.map((h) => ({ ...h })) });
    }
  };

  const [deteccionKey, setDeteccionKey] = useState(null);
  const [deteccionSel, setDeteccionSel] = useState({});
  const tareaDeteccion = tareas.find((t) => t.key === deteccionKey) || null;

  useEffect(() => {
    if (tareaDeteccion?.deteccion) {
      const init = {};
      tareaDeteccion.deteccion.sugerencias.forEach((_, idx) => { init[idx] = true; });
      setDeteccionSel(init);
    }
  }, [deteccionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Analiza el detalle libre de una tarea (al salir del campo) buscando servicios/repuestos
  // ya existentes mencionados en el texto, para sugerir cargarlos correctamente. Devuelve la
  // promesa en curso (o null si no hace falta analizar) para que "guardar" pueda esperarla.
  const analizarDetalle = (t) => {
    const texto = (t.detalle || "").trim();
    if (texto === t.detalleAnalizado) return analisisPendientesRef.current[t.key] || null;
    const promesa = (async () => {
      if (texto.length < 8) {
        actualizarTareas((prev) => prev.map((x) => (x.key === t.key ? { ...x, detalleAnalizado: texto, deteccion: null } : x)));
        return { key: t.key, texto, deteccion: null };
      }
      try {
        const { data } = await api.post("/detalle/analizar",
          { texto, modelo_id: vehiculo?.modelo_id || null, servicio_id: t.servicio_id || null });
        const deteccion = data.sugerencias?.length ? data : null;
        actualizarTareas((prev) => prev.map((x) => (x.key === t.key
          ? { ...x, detalleAnalizado: texto, deteccion, deteccionIgnorada: false }
          : x)));
        return { key: t.key, texto, deteccion };
      } catch {
        return { key: t.key, texto, deteccion: null }; // nunca bloquea el guardado
      }
    })();
    analisisPendientesRef.current[t.key] = promesa;
    promesa.finally(() => { delete analisisPendientesRef.current[t.key]; });
    return promesa;
  };

  // Tras aplicar/ignorar una sugerencia durante el flujo de "Revisar" (al intentar guardar),
  // pasa a la siguiente tarea con sugerencias pendientes o reintenta guardar si ya no quedan.
  const continuarRevision = () => {
    const conSugerencias = tareasRef.current.filter((x) => x.deteccion && !x.deteccionIgnorada);
    if (conSugerencias.length) {
      setDeteccionKey(conSugerencias[0].key);
    } else {
      revisandoParaGuardarRef.current = false;
      guardar();
    }
  };

  const ignorarDeteccion = () => {
    actualizarTareas((prev) => prev.map((x) => (x.key === deteccionKey ? { ...x, deteccionIgnorada: true } : x)));
    setDeteccionKey(null);
    if (revisandoParaGuardarRef.current) setTimeout(continuarRevision, 50);
  };

  const aplicarDeteccion = async () => {
    const t = tareaDeteccion;
    if (!t?.deteccion) return;
    const sugs = t.deteccion.sugerencias.filter((_, idx) => deteccionSel[idx]);
    const resto = (t.deteccion.resto || "").trim();
    setDeteccionKey(null);
    setGuardando(true);
    const keysResaltar = [];
    try {
      for (const s of sugs.filter((s) => s.tipo === "servicio")) {
        const nueva = { ...nuevaTarea(), recienAgregada: true };
        keysResaltar.push(nueva.key);
        actualizarTareas((prev) => [...prev, nueva]);
        await elegirServicio(nueva.key, s.id); // eslint-disable-line no-await-in-loop
      }

      const repuestosElegidos = sugs.filter((s) => s.tipo === "repuesto");
      if (repuestosElegidos.length) {
        const cambioPartes = servicios.find((sv) => sv.solicita_repuestos &&
          sv.nombre.trim().toLowerCase() === "cambio de partes");
        if (cambioPartes) {
          actualizarTareas((prev) => {
            const existente = prev.find((x) => x.servicio_id === cambioPartes.id);
            const nuevosItems = repuestosElegidos.map((r) =>
              ({ ref_id: r.id, codigo: r.codigo || "", nombre: r.nombre, categoria: "", cantidad: 1 }));
            if (existente) {
              keysResaltar.push(existente.key);
              return prev.map((x) => (x.key === existente.key
                ? { ...x, recienAgregada: true, repuestos: [...(x.repuestos || []),
                    ...nuevosItems.filter((n) => !(x.repuestos || []).some((rr) => rr.ref_id === n.ref_id))] }
                : x));
            }
            const nueva = { ...nuevaTarea(), servicio_id: cambioPartes.id, repuestos: nuevosItems, recienAgregada: true };
            keysResaltar.push(nueva.key);
            return [...prev, nueva];
          });
        } else {
          toast.error('No se encontró el servicio "Cambio de partes" para agregar los repuestos sugeridos');
        }
      }

      const esOtros = (servicios.find((sv) => sv.id === t.servicio_id)?.nombre || "").trim().toLowerCase() === "otros";
      if (esOtros && !resto) {
        actualizarTareas((prev) => prev.filter((x) => x.key !== t.key));
      } else {
        actualizarTareas((prev) => prev.map((x) => (x.key === t.key
          ? { ...x, detalle: resto, detalleAnalizado: resto, deteccion: null } : x)));
      }

      if (keysResaltar.length) {
        setTimeout(() => actualizarTareas((prev) =>
          prev.map((x) => (keysResaltar.includes(x.key) ? { ...x, recienAgregada: false } : x))), 3000);
      }
      toast.success("Sugerencias aplicadas. Revise el trabajo y pulse Guardar");

      const siguiente = tareasRef.current.find((x) => x.key !== t.key && x.deteccion && !x.deteccionIgnorada);
      if (siguiente) setDeteccionKey(siguiente.key);
    } finally {
      setGuardando(false);
    }
  };

  const resolverCronoElegido = async (cronograma_id) => {
    const p = cronoPickerActivo;
    cerrarVentanaActual();
    if (!p) return;
    try {
      const { data } = await api.get("/cronogramas/aplicable", {
        params: { servicio_id: p.servicioId, vehiculo_id: vehiculoId, cronograma_id, ...(kmNum !== null ? { km: kmNum } : {}) },
      });
      if (data.encontrado) aplicarCrono(p.key, servicios.find((s) => s.id === p.servicioId), data);
    } catch { /* ignore */ }
  };

  const elegirServicio = async (key, servicioId, opts = {}) => {
    actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, servicio_id: servicioId, hijos: null, plan: null, grupos: [] } : t)));
    if (!servicioId) return;
    if (cuponServicioIds.has(servicioId)) {
      encolarVentana({ tipo: "cupon_datos", key, cuponIncompleto: !!opts.cuponIncompleto });
    }
    if (garantiaServicioIds.has(servicioId)) {
      encolarVentana({ tipo: "garantia_reporte", key });
    }
    const svc = servicios.find((s) => s.id === servicioId);
    if (svc?.solicita_repuestos) encolarVentana({ tipo: "picker", key });
    // Precedencia: si el servicio resuelve un cronograma aplicable al vehículo, abrir PRIMERO
    // el PASO 1 (servicios del cronograma). NO abrir aquí la ventana de repuestos.
    let cronoAplicable = false;
    if (vehiculoId) {
      try {
        const { data } = await api.get("/cronogramas/aplicable", {
          params: { servicio_id: servicioId, vehiculo_id: vehiculoId, ...(kmNum !== null ? { km: kmNum } : {}) },
        });
        if (data.encontrado) {
          cronoAplicable = true;
          aplicarCrono(key, svc, data);
        } else if ((data.opciones || []).length > 1) {
          cronoAplicable = true; // varios cronogramas: dejar elegir (no abrir repuestos)
          encolarVentana({ tipo: "cronoPicker", key, servicioId, opciones: data.opciones, modeloNombre: data.modelo_nombre });
        } else if (svc?.abre_cronograma) {
          toast.info(`El modelo ${data.modelo_nombre || "del vehículo"} no tiene cronograma de mantenimiento asignado`);
        }
      } catch { /* sin cronograma aplicable */ }
    }
    // Solo si NO hay cronograma aplicable: abrir directo la ventana de repuestos de la plantilla directa.
    if (!cronoAplicable) {
      try {
        const { data } = await api.post("/servicios/plan-repuestos", {
          servicio_id: servicioId, hijos_ids: [], modelo_id: vehiculo?.modelo_id || null,
        });
        const grupos = data.grupos || [];
        if (grupos.length) {
          actualizarTareas((prev) => prev.map((t) => (t.key === key
            ? { ...t, grupos, sinGrupoTarifa: (data.avisos || []).includes("sin_grupo_tarifa") } : t)));
          encolarVentana({ tipo: "plantilla", key });
        }
      } catch { /* servicio sin plantilla */ }
    }
  };

  const tareaPlantilla = tareas.find((t) => t.key === plantillaKeyActivo) || null;
  const servicioPlantilla = tareaPlantilla ? servicios.find((s) => s.id === tareaPlantilla.servicio_id) : null;
  const confirmarPlantilla = (gruposFinal) => {
    const key = plantillaKeyActivo;
    actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, grupos: gruposFinal } : t)));
  };

  const tareaPicker = tareas.find((t) => t.key === pickerKeyActivo) || null;
  const servicioPicker = tareaPicker ? servicios.find((s) => s.id === tareaPicker.servicio_id) : null;
  const confirmarPicker = (items) => {
    const key = pickerKeyActivo;
    actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, repuestos: items } : t)));
  };

  const abrirModalHijos = (t) => {
    encolarVentana({ tipo: "modal", key: t.key, hijos: t.hijos.map((h) => ({ ...h })) });
  };

  const guardarModalHijos = () => {
    const key = modalKeyActivo;
    actualizarTareas((prev) => prev.map((t) => (t.key === key ? { ...t, hijos: modalHijos } : t)));
    cerrarVentanaActual();
  };

  // Paso 1 -> Paso 2: resuelve los grupos (plantilla del servicio principal + una por cada
  // servicio del cronograma seleccionado que tenga plantillas_asociadas), con FRT/mano de obra
  // efectiva ("incluye" entre grupos) y "ya incluido" para repuestos repetidos entre grupos.
  const irAPaso2 = async () => {
    const key = modalKeyActivo;
    const t = tareas.find((x) => x.key === key);
    actualizarTareas((prev) => prev.map((x) => (x.key === key ? { ...x, hijos: modalHijos } : x)));
    const seleccionados = modalHijos.filter((h) => h.se_realiza && h.accion !== "revisar").map((h) => h.hijo_id);
    cerrarVentanaActual();
    if (!t?.servicio_id) return;
    try {
      const { data } = await api.post("/servicios/plan-repuestos", {
        servicio_id: t.servicio_id, hijos_ids: seleccionados, modelo_id: vehiculo?.modelo_id || null,
      });
      const grupos = data.grupos || [];
      if (!grupos.length) return;
      // Conservar decisiones previas de "Lo traemos / Lo trae el cliente" por repuesto.
      const prevDec = {};
      (t?.grupos || []).forEach((g) => g.items.forEach((it) => { prevDec[it.ref_id || it.repuesto_id] = it.aportado_cliente; }));
      const gruposUI = grupos.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it,
        ref_id: it.repuesto_id, aportado_cliente: prevDec[it.repuesto_id] ?? false })) }));
      actualizarTareas((prev) => prev.map((x) => (x.key === key
        ? { ...x, grupos: gruposUI, sinGrupoTarifa: (data.avisos || []).includes("sin_grupo_tarifa") } : x)));
      encolarVentana({ tipo: "plantilla", key });
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const volverAPaso1 = () => {
    const t = tareas.find((x) => x.key === plantillaKeyActivo);
    if (t?.hijos) actualizarVentana({ tipo: "modal", key: t.key, hijos: t.hijos.map((h) => ({ ...h })) });
    else cerrarVentanaActual();
  };

  const tareaModal = tareas.find((t) => t.key === modalKeyActivo) || null;
  const servicioModal = tareaModal ? servicios.find((s) => s.id === tareaModal.servicio_id) : null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const pendientes = tareas.map((t) => analizarDetalle(t)).filter(Boolean);
      let resultados = [];
      if (pendientes.length) {
        const settled = await Promise.race([
          Promise.allSettled(pendientes),
          new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        if (settled) resultados = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      }
      const deteccionesResueltas = new Map(resultados.map((r) => [r.key, r.deteccion]));
      const conSugerencias = tareasRef.current.filter((t) => {
        const deteccion = deteccionesResueltas.has(t.key) ? deteccionesResueltas.get(t.key) : t.deteccion;
        return deteccion && !t.deteccionIgnorada;
      });
      if (conSugerencias.length) {
        setGuardando(false);
        setAvisoSugerencias(conSugerencias.length);
        return;
      }
      await guardarDefinitivo();
    } catch (err) {
      setGuardando(false);
      toast.error(errorMsg(err));
    }
  };

  const revisarSugerencias = () => {
    setAvisoSugerencias(null);
    revisandoParaGuardarRef.current = true;
    continuarRevision();
  };

  const guardarIgual = async () => {
    setAvisoSugerencias(null);
    await guardarDefinitivo();
  };

  const guardarDefinitivo = async () => {
    const tareasActuales = tareasRef.current;
    const e = {};
    if (!vehiculoId) e.vehiculo_id = "Seleccione vehículo";
    if (!aceite) e.nivel_aceite = "Seleccione nivel de aceite";
    if (!gasolina) e.nivel_gasolina = "Seleccione nivel de gasolina";
    if (!conCita) e.con_cita = "Indique si el cliente viene con cita previa";
    if (vehiculoMide && medir) {
      if (km === "") e.kilometraje = "Ingrese kilometraje";
      else if (kmInvalido) e.kilometraje = `Debe ser mayor o igual al último registrado (${fmtMiles(kmAnterior)})`;
    }
    if (tareasActuales.length === 0) e.tareas = "Agregue al menos una tarea";
    else if (tareasActuales.some((t) => !t.servicio_id)) e.tareas = "Seleccione la tarea en todas las filas";
    if (tareasActuales.some((t) => cuponServicioIds.has(t.servicio_id)) && !datosCupon) {
      e.datos_cupon = "Complete los datos del cupón (botón \"Datos del cupón\" en la tarea)";
    }
    if (tareasActuales.some((t) => garantiaServicioIds.has(t.servicio_id)) && !garantiaReporte) {
      e.garantia = "Complete el reporte del cliente (ventana de reporte de garantía Honda)";
    }
    if (trabajoActivo) e.vehiculo_id = `Este vehículo ya tiene un trabajo activo (Orden #${numTrabajo(trabajoActivo.numero)})`;
    setErrores(e);
    if (Object.values(e).some(Boolean)) { setGuardando(false); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/trabajos", {
        vehiculo_id: vehiculoId,
        kilometraje: vehiculoMide && medir ? kmNum : null,
        medir_kilometraje: vehiculoMide && medir,
        nivel_aceite: aceite,
        nivel_gasolina: gasolina,
        con_cita: conCita === "si",
        tareas: tareasActuales.map(({ servicio_id, detalle, prioridad, hijos }) => ({
          servicio_id, detalle, prioridad,
          hijos: hijos ? hijos.map(({ hijo_id, accion, se_realiza }) => ({ hijo_id, accion, se_realiza })) : null,
        })),
        arrastrados,
        datos_cupon: datosCupon,
        garantia: garantiaReporte,
      });
      // Repuestos de tareas: todo lo creado en este flujo de INGRESO (servicio principal,
      // servicios generados por el cronograma y sus repuestos; orígenes "ingreso", "ingreso:<id>"
      // y "plantilla:<id>") se guarda como "aprobado" (la decisión ya se tomó en las ventanas de
      // este flujo). Lo que se agregue después desde Solicitud sigue entrando "pendiente".
      // Cada grupo NO principal crea su propia línea "servicio" (mano de obra calculada) y sus
      // repuestos quedan con origen apuntando a esa línea, no al servicio principal.
      const repsPicker = tareasActuales.flatMap((t) => (t.repuestos || []).map((r) => ({ ...r, servicio_id: t.servicio_id, estado: "aprobado", aportado_cliente: false })));
      const gruposTareas = tareasActuales.flatMap((t) => (t.grupos || []).map((g) => ({ ...g, tareaServicioId: t.servicio_id })));
      if (repsPicker.length || gruposTareas.length) {
        try {
          const sinc = await api.post(`/trabajos/${data.id}/solicitudes/sincronizar`)
            .catch(() => api.get(`/trabajos/${data.id}`));
          const base = (sinc.data.solicitudes || []).map(({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit }) =>
            ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit }));
          const yaServicio = new Set(base.filter((s) => s.tipo === "servicio").map((s) => s.ref_id));
          const serviciosNuevos = [];
          const lineasRepuesto = base.filter((s) => s.tipo === "repuesto").map((s) => ({ ...s, _fijarDecision: true }));
          for (const r of repsPicker) {
            lineasRepuesto.push({ id: null, tipo: "repuesto", ref_id: r.ref_id, codigo: r.codigo || "", nombre: r.nombre,
              categoria: r.categoria || "", cantidad: r.cantidad || 1, prioridad: "media",
              origen: `plantilla:${r.servicio_id}`, plantilla_id: null, mano_obra: null,
              estado: r.estado, aportado_cliente: !!r.aportado_cliente, ya_incluido: false, opcional: false,
              cantidad_maxima: null, incluido_en_kit: null });
          }
          for (const g of gruposTareas) {
            if (!g.principal && !yaServicio.has(g.servicio_id)) {
              serviciosNuevos.push({ id: null, tipo: "servicio", ref_id: g.servicio_id, codigo: "", nombre: g.nombre,
                categoria: "", cantidad: 1, prioridad: "media", origen: `ingreso:${g.tareaServicioId}`, plantilla_id: g.plantilla_id,
                mano_obra: g.mano_obra, estado: "aprobado", aportado_cliente: false, ya_incluido: false, opcional: false });
              yaServicio.add(g.servicio_id);
            }
            // Repuestos repetidos entre grupos/tareas (mismo repuesto_id, p.ej. motor/transmisión):
            // se recorren en orden sumando cantidades; si alguno trae "cantidad_maxima" (del
            // catálogo), lo que pase ese tope queda "Ya incluido · Máximo N por trabajo". Sin
            // tope entra siempre normal (ambos se cobran).
            for (const it of g.items) {
              const refId = it.ref_id || it.repuesto_id;
              lineasRepuesto.push({ id: null, tipo: "repuesto", ref_id: refId, codigo: it.codigo || "", nombre: it.nombre,
                categoria: it.categoria || "", cantidad: it.cantidad || 1, prioridad: "media",
                origen: `plantilla:${g.servicio_id}`, plantilla_id: g.plantilla_id, mano_obra: null,
                estado: "aprobado", aportado_cliente: !!it.aportado_cliente, ya_incluido: !!it.ya_incluido,
                opcional: !!it.opcional, cantidad_maxima: it.cantidad_maxima ?? null,
                incluido_en_kit: it.incluido_en_kit ?? null, _fijarDecision: !!it.ya_incluido });
            }
          }
          const ridsLote = [...new Set(lineasRepuesto.map((l) => l.ref_id).filter(Boolean))];
          const componentesPorKit = ridsLote.length
            ? (await api.post("/repuestos/componentes", { ids: ridsLote }).catch(() => ({ data: {} }))).data
            : {};
          const decididas = aplicarLimitePorTrabajo(lineasRepuesto, componentesPorKit)
            .map(({ _fijarDecision, ...l }) => l);
          await api.put(`/trabajos/${data.id}/solicitudes`, { solicitudes: [...base.filter((s) => s.tipo === "servicio"), ...serviciosNuevos, ...decididas] });
        } catch (e2) {
          toast.error("Trabajo creado, pero fallaron los repuestos de una tarea: " + errorMsg(e2));
        }
      }
      // Congela la foto de la boleta de ingreso al cierre de este flujo: la boleta firmada por
      // el cliente ya no cambia aunque después se agregue, apruebe, modifique o borre algo.
      await api.post(`/trabajos/${data.id}/boleta-ingreso/congelar`).catch(() => {});
      const tieneCupon = tienePermiso("honda.ordenes") && (data.tareas || []).some((t) => cuponServicioIds.has(t.servicio_id));
      if (tieneCupon) {
        hondaTrabajoRef.current = data;
        setHondaOrdenEstado("preguntar");
      } else {
        finalizarGuardado(data);
      }
    } catch (err) {
      toast.error(errorMsg(err));
    } finally { setGuardando(false); }
  };

  const finalizarGuardado = (data) => {
    setUltimoTrabajo(data);
    toast.success("Trabajo ingresado de forma exitosa");
    setExito(true);
    setVehiculoId(""); setMedir(true); setKm(""); setAceite(""); setGasolina(""); setConCita("");
    actualizarTareas([nuevaTarea()]); setErrores({});
    setDatosCupon(null);
    setGarantiaReporte(null);
    setClienteFiltroId(""); setAvisoBusqueda(null); setTrabajoActivo(null);
    setPendientesAnt([]); setArrastrados([]); setRepArrastrados([]);
    cargarCatalogos();
    // Fotos del trabajo (etapa 2): las sube el asesor. Se puede cerrar sin tomar fotos.
    if (tienePermiso("trabajos.fotos")) setFotosTrabajoId(data.id);
  };

  // Tras guardar un trabajo con servicio de cupón (solo ADMIN): pregunta si se desea encolar la
  // orden de cupón en Honda. Si falta el contacto (cliente jurídico sin contacto), pide el nombre
  // y reenvía. Cualquier otro resultado (éxito, error o "No") continúa con el flujo normal.
  const hondaSolicitar = async (contactoNombre, guardarContacto) => {
    const trabajoId = hondaTrabajoRef.current?.id;
    setHondaEnviando(true);
    try {
      await api.post(`/trabajos/${trabajoId}/honda-orden`,
        { contacto_nombre: contactoNombre || null, guardar_contacto: !!guardarContacto });
      toast.success("Orden de cupón Honda encolada");
      setHondaOrdenEstado(null);
      finalizarGuardado(hondaTrabajoRef.current);
    } catch (err) {
      if (err?.response?.data?.detail?.falta === "contacto") {
        setHondaContactoNombre(""); setHondaGuardarContacto(true);
        setHondaOrdenEstado("contacto");
      } else {
        toast.error(errorMsg(err));
        setHondaOrdenEstado(null);
        finalizarGuardado(hondaTrabajoRef.current);
      }
    } finally { setHondaEnviando(false); }
  };

  const hondaNo = () => { setHondaOrdenEstado(null); finalizarGuardado(hondaTrabajoRef.current); };
  const hondaSi = () => hondaSolicitar(null, false);
  const hondaEnviarContacto = () => hondaSolicitar(hondaContactoNombre, hondaGuardarContacto);

  return (
    <div data-testid="trabajo-ingresar-page" className="space-y-4">
      {exito && (
        <div data-testid="trabajo-exito-alert"
          className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle2 size={18} className="text-green-600 shrink-0" />
          Trabajo ingresado de forma exitosa
          {ultimoTrabajo && (
            <button data-testid="trabajo-boleta-ingreso-btn"
              onClick={() => descargarPdf(`/trabajos/${ultimoTrabajo.id}/boleta-pdf`, `boleta_${numTrabajo(ultimoTrabajo.numero)}.pdf`)}
              className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-green-800 underline hover:text-green-900">
              <FileText size={13} /> Boleta de ingreso (PDF)
            </button>
          )}
          <button data-testid="trabajo-exito-cerrar" onClick={() => setExito(false)} className="ml-auto text-green-700 hover:text-green-900">
            <X size={16} />
          </button>
        </div>
      )}

      {avisoBusqueda && (
        <div data-testid="trabajo-busqueda-aviso"
          className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${
            avisoBusqueda.tipo === "buscando" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-amber-200 bg-amber-50 text-amber-800"
          }`}>
          {avisoBusqueda.texto}
          {avisoBusqueda.tipo === "sin_vehiculos" && (
            <Button size="sm" variant="outline" data-testid="trabajo-crear-vehiculo-btn"
              className="ml-2 h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
              onClick={() => navigate("/vehiculos", { state: { clienteId: avisoBusqueda.clienteId } })}>
              Crear vehículo
            </Button>
          )}
          {avisoBusqueda.tipo !== "buscando" && (
            <button data-testid="trabajo-busqueda-aviso-cerrar" onClick={() => setAvisoBusqueda(null)}
              className="ml-auto text-amber-700 hover:text-amber-900"><X size={14} /></button>
          )}
        </div>
      )}

      {trabajoActivo && (
        <div data-testid="trabajo-activo-aviso"
          className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          Este vehículo ya tiene un trabajo activo (Orden #{numTrabajo(trabajoActivo.numero)}).
          Debe cerrarlo o anularlo antes de crear uno nuevo.
          <Button size="sm" variant="outline" data-testid="trabajo-activo-ver-btn"
            className="ml-2 h-7 border-red-400 text-red-800 hover:bg-red-100"
            onClick={() => navigate("/trabajos/activos")}>
            Ver trabajo activo
          </Button>
        </div>
      )}

      {pendientesAnt.length > 0 && (
        <div data-testid="ingresar-pendientes-anteriores-panel"
          className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3">
          <p className="text-sm font-semibold text-amber-800 mb-2">
            Pendientes de trabajos anteriores ({pendientesAnt.length})
          </p>
          {pendientesAnt.map((it, i) => (
            <div key={it.solicitud.id} data-testid={`ingresar-pendiente-anterior-${i}`}
              className="flex items-center gap-2 py-1.5 border-t border-amber-100 first:border-0 text-sm">
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                it.solicitud.tipo === "servicio" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}`}>
                {it.solicitud.tipo === "servicio" ? "Servicio" : "Repuesto"}
              </span>
              <span className="truncate text-slate-700">
                {it.solicitud.nombre}
                {it.solicitud.tipo === "repuesto" && it.solicitud.cantidad > 1 ? ` x${it.solicitud.cantidad}` : ""}
              </span>
              <span className="shrink-0 text-xs text-slate-400">de Orden #{numTrabajo(it.numero_origen)}</span>
              <div className="ml-auto flex gap-1.5 shrink-0">
                <Button size="sm" data-testid={`ingresar-pendiente-aprobar-${i}`}
                  className="h-7 bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => aprobarPendiente(it)}>
                  Aprobar
                </Button>
                <Button size="sm" variant="outline" data-testid={`ingresar-pendiente-rechazar-${i}`}
                  className="h-7 border-pink-300 text-pink-700 hover:bg-pink-50"
                  onClick={() => rechazarPendiente(it)}>
                  Rechazar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Panel titulo="Información del cliente" testId="trabajo-cliente-panel">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Lectura label="Nombre o Razón Social" value={cliente?.nombre} testId="trabajo-cliente-nombre" />
          <Lectura label="Primer Apellido" value={cliente?.primer_apellido} testId="trabajo-cliente-primer-apellido" />
          <Lectura label="Segundo Apellido" value={cliente?.segundo_apellido} testId="trabajo-cliente-segundo-apellido" />
          <Lectura label="Número de ID" value={cliente?.person_id} testId="trabajo-cliente-person-id" />
        </div>
      </Panel>

      <Panel titulo="Información del vehículo" testId="trabajo-vehiculo-panel">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Campo label="Vehículo" obligatorio error={errores.vehiculo_id} testId="trabajo-vehiculo">
            <Combobox testId="trabajo-vehiculo-combobox" value={vehiculoId} onChange={seleccionarVehiculo}
              placeholder="Seleccione Vehículo" options={opcionesVehiculo} />
            {clienteFiltro && (
              <p data-testid="trabajo-filtro-cliente" className="text-xs text-slate-500">
                Vehículos de <b>{nombreCli(clienteFiltro)}</b>
                <button type="button" data-testid="trabajo-filtro-quitar" onClick={quitarFiltro}
                  className="ml-2 font-medium text-indigo-600 underline">Ver todos</button>
              </p>
            )}
          </Campo>
          <Campo label="Kilometraje" obligatorio={vehiculoMide && medir} error={errores.kilometraje} testId="trabajo-kilometraje">
            <div className="flex items-center gap-3">
              <Input data-testid="trabajo-kilometraje-input" inputMode="numeric"
                value={km === "" ? "" : fmtMiles(km)}
                onChange={(e) => setKm(e.target.value.replace(/\D/g, "").slice(0, 9))}
                onBlur={() => { if (vehiculoMide && medir && kmNum !== null && !kmInvalido) consultarCupones(vehiculoId, kmNum); }}
                disabled={!vehiculoMide || !medir} placeholder="Ej: 12 500"
                className={kmInvalido ? "border-red-400" : ""} />
              <label className="flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap">
                <Checkbox data-testid="trabajo-medir-checkbox" checked={medir} disabled={!vehiculoMide}
                  onCheckedChange={(v) => { setMedir(!!v); setErrores((er) => ({ ...er, kilometraje: undefined })); }} />
                Medir
              </label>
            </div>
            {vehiculoMide && medir && vehiculo && (
              <p className="text-xs text-slate-500">
                Anterior: {fmtMiles(kmAnterior)}
                {transcurrido !== null && (
                  <span data-testid="trabajo-km-transcurrido" className="ml-2 text-teal-600 font-medium">
                    Kilometraje transcurrido: {fmtMiles(transcurrido)}
                  </span>
                )}
              </p>
            )}
          </Campo>
          <Campo label="Nivel de Aceite" obligatorio error={errores.nivel_aceite} testId="trabajo-aceite">
            <Select value={aceite} onValueChange={(v) => { setAceite(v); setErrores((e) => ({ ...e, nivel_aceite: undefined })); }}>
              <SelectTrigger data-testid="trabajo-aceite-select"><SelectValue placeholder="Seleccione" /></SelectTrigger>
              <SelectContent>
                {NIVEL_ACEITE.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Campo>
          <Campo label="Nivel de Gasolina" obligatorio error={errores.nivel_gasolina} testId="trabajo-gasolina">
            <Select value={gasolina} onValueChange={(v) => { setGasolina(v); setErrores((e) => ({ ...e, nivel_gasolina: undefined })); }}>
              <SelectTrigger data-testid="trabajo-gasolina-select"><SelectValue placeholder="Seleccione" /></SelectTrigger>
              <SelectContent>
                {NIVEL_GASOLINA.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Campo>
          <Campo label="¿El cliente viene con cita previa?" obligatorio error={errores.con_cita} testId="trabajo-con-cita">
            <Select value={conCita} onValueChange={(v) => { setConCita(v); setErrores((e) => ({ ...e, con_cita: undefined })); }}>
              <SelectTrigger data-testid="trabajo-con-cita-select"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
              <SelectContent>
                {CON_CITA_OPCIONES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Campo>
        </div>
      </Panel>

      <Panel titulo="Trabajos a Realizar" testId="trabajo-tareas-panel">
        {repArrastrados.length > 0 && (
          <div data-testid="ingresar-rep-arrastrados" className="mb-3 rounded-md bg-teal-50/70 border border-teal-100 px-3 py-2">
            <p className="text-xs font-semibold text-teal-800 mb-1">Repuestos aprobados de trabajos anteriores (se incluirán al guardar):</p>
            <div className="flex flex-wrap gap-1.5">
              {repArrastrados.map((it) => (
                <span key={it.solicitud.id} data-testid={`ingresar-rep-arrastrado-${it.solicitud.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white border border-teal-200 px-2.5 py-0.5 text-xs text-teal-800">
                  {it.solicitud.nombre}{it.solicitud.cantidad > 1 ? ` x${it.solicitud.cantidad}` : ""}
                  <button type="button" title="Quitar" onClick={() => quitarRepArrastrado(it)}
                    className="text-teal-500 hover:text-teal-800"><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-between items-center mb-3">
          <Button variant="outline" data-testid="trabajo-agregar-tarea-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
            onClick={() => actualizarTareas([...tareas, nuevaTarea()])}>
            <Plus size={15} className="mr-1" /> Agregar tarea
          </Button>
          {errores.tareas && <p data-testid="trabajo-tareas-error" className="text-xs text-red-500">{errores.tareas}</p>}
          {errores.datos_cupon && <p data-testid="trabajo-datos-cupon-error" className="text-xs text-red-500">{errores.datos_cupon}</p>}
          {errores.garantia && <p data-testid="trabajo-garantia-error" className="text-xs text-red-500">{errores.garantia}</p>}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="trabajo-tareas-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold w-[35%]">Tarea</th>
                <th className="py-2.5 pr-3 font-semibold w-[40%]">Detalle</th>
                <th className="py-2.5 pr-3 font-semibold">Prioridad</th>
                <th className="py-2.5 text-right font-semibold">Acción</th>
              </tr>
            </thead>
            <tbody>
              {tareas.map((t, i) => (
                <tr key={t.key} data-testid={`trabajo-tarea-row-${i}`}
                  className={`border-b transition-colors duration-500 ${t.recienAgregada ? "border-amber-300 bg-amber-50 ring-1 ring-amber-300" : "border-slate-100"}`}>
                  <td className="py-2 pr-3 align-top">
                    <Combobox testId={`trabajo-tarea-combobox-${i}`} value={t.servicio_id}
                      onChange={(v) => elegirServicio(t.key, v)} placeholder="Seleccione Tarea"
                      options={servicios.map((s) => ({ value: s.id, label: s.nombre }))} />
                    {t.hijos && (
                      <button type="button" data-testid={`trabajo-hijos-reabrir-${i}`}
                        onClick={() => abrirModalHijos(t)}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-800">
                        <span className="font-bold">&gt;&gt;&gt;</span>
                        {t.plan || servicios.find((s) => s.id === t.servicio_id)?.nombre || ""}
                        <span className="text-slate-400 font-normal">
                          ({t.hijos.filter((h) => h.se_realiza).length}/{t.hijos.length})
                        </span>
                      </button>
                    )}
                    {servicios.find((s) => s.id === t.servicio_id)?.solicita_repuestos && (
                      <button type="button" data-testid={`trabajo-repuestos-btn-${i}`}
                        onClick={() => encolarVentana({ tipo: "picker", key: t.key })}
                        className="mt-1 flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                        <PackagePlus size={13} /> Repuestos a cambiar
                        <span className="text-slate-400 font-normal">({(t.repuestos || []).length})</span>
                      </button>
                    )}
                    {(t.grupos || []).length > 0 && (
                      <button type="button" data-testid={`trabajo-plantilla-reps-btn-${i}`}
                        onClick={() => encolarVentana({ tipo: "plantilla", key: t.key })}
                        className="mt-1 flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-800">
                        <PackageCheck size={13} /> Repuestos del servicio
                        <span className="text-slate-400 font-normal">
                          ({t.grupos.length} servicio{t.grupos.length > 1 ? "s" : ""}, {t.grupos.reduce((a, g) => a + g.items.length, 0)} repuesto{t.grupos.reduce((a, g) => a + g.items.length, 0) !== 1 ? "s" : ""})
                        </span>
                      </button>
                    )}
                    {cuponServicioIds.has(t.servicio_id) && (
                      <button type="button" data-testid={`trabajo-datos-cupon-btn-${i}`}
                        onClick={() => encolarVentana({ tipo: "cupon_datos", key: t.key, cuponIncompleto: false })}
                        className={`mt-1 flex items-center gap-1 text-xs font-medium ${datosCupon ? "text-teal-600 hover:text-teal-800" : "text-amber-600 hover:text-amber-700"}`}>
                        <FileText size={13} /> Datos del cupón {datosCupon ? "" : "(pendiente)"}
                      </button>
                    )}
                    {t.deteccion && !t.deteccionIgnorada && (
                      <button type="button" data-testid={`trabajo-deteccion-btn-${i}`}
                        onClick={() => setDeteccionKey(t.key)}
                        title="El detalle menciona servicios o repuestos que existen en el sistema"
                        className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700">
                        <AlertTriangle size={13} /> Sugerencias del detalle
                      </button>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <Input data-testid={`trabajo-tarea-detalle-${i}`} value={t.detalle} maxLength={120}
                      onChange={(e) => setTarea(t.key, "detalle", e.target.value)}
                      onBlur={() => analizarDetalle(t)}
                      placeholder="(opcional) Puede ingresar una observación" />
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <Select value={t.prioridad} onValueChange={(v) => setTarea(t.key, "prioridad", v)}>
                      <SelectTrigger data-testid={`trabajo-tarea-prioridad-${i}`} className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORIDADES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 align-top">
                    <div className="flex justify-end h-9 items-center">
                      <button data-testid={`trabajo-tarea-eliminar-${i}`} title="Eliminar"
                        onClick={() => actualizarTareas(tareas.filter((x) => x.key !== t.key))}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tareas.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-slate-400">
                  Agregue al menos una tarea con el botón "+ Agregar tarea"
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-6">
          <Button data-testid="trabajo-guardar-btn" onClick={guardar} disabled={guardando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </Panel>

      <Dialog open={avisoSugerencias != null} onOpenChange={(o) => { if (!o) setAvisoSugerencias(null); }}>
        <DialogContent data-testid="aviso-sugerencias-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              Hay sugerencias para {avisoSugerencias} tarea{avisoSugerencias === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            El detalle de {avisoSugerencias === 1 ? "esa tarea" : "esas tareas"} menciona servicios o repuestos
            que ya existen en el sistema. ¿Deseás revisarlas antes de guardar?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="aviso-sugerencias-guardar-igual-btn" onClick={guardarIgual}>
              Guardar igual
            </Button>
            <Button data-testid="aviso-sugerencias-revisar-btn" onClick={revisarSugerencias}
              className="bg-amber-600 hover:bg-amber-700 text-white">
              Revisar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deteccionKey} onOpenChange={(o) => { if (!o) { setDeteccionKey(null); revisandoParaGuardarRef.current = false; } }}>
        <DialogContent data-testid="deteccion-detalle-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" /> Sugerencias para esta tarea
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(tareaDeteccion?.deteccion?.sugerencias || []).map((s, idx) => (
              <label key={`${s.tipo}-${s.id}`} data-testid={`deteccion-item-${idx}`}
                className="flex items-start gap-2 rounded-lg border border-slate-200 p-2.5 text-sm cursor-pointer">
                <Checkbox data-testid={`deteccion-check-${idx}`} checked={!!deteccionSel[idx]}
                  onCheckedChange={(v) => setDeteccionSel((prev) => ({ ...prev, [idx]: !!v }))} className="mt-0.5" />
                <span className="flex-1 text-slate-700">
                  {s.tipo === "servicio" ? `Agregar servicio ${s.nombre}` : `Agregar ${s.nombre} (servicio Cambio de partes)`}
                </span>
              </label>
            ))}
            {tareaDeteccion?.deteccion?.resto && (
              <p data-testid="deteccion-resto" className="text-xs text-slate-500 pt-1">
                Se conserva como detalle: "{tareaDeteccion.deteccion.resto}"
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="deteccion-ignorar-btn" onClick={ignorarDeteccion}>Ignorar</Button>
            <Button data-testid="deteccion-aplicar-btn" onClick={aplicarDeteccion}
              className="bg-amber-600 hover:bg-amber-700 text-white">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modalKeyActivo} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}>
        <DialogContent data-testid="trabajo-hijos-modal" className="sm:max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-heading">
              {tareaModal?.plan || `Plan de Mantenimiento ${servicioModal?.nombre || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-x-6 max-h-[55vh] overflow-y-auto overscroll-contain pr-1">
            {[0, 1].map((col) => {
              const mitad = Math.ceil(modalHijos.length / 2);
              const items = col === 0 ? modalHijos.slice(0, mitad) : modalHijos.slice(mitad);
              return (
                <div key={col}>
                  <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-slate-200 pb-1.5 text-[11px] font-semibold uppercase text-slate-500">
                    <span>Tarea</span><span>Acción</span><span>¿Se realiza?</span>
                  </div>
                  {items.map((h) => {
                    const idx = modalHijos.indexOf(h);
                    return (
                      <div key={h.hijo_id} data-testid={`trabajo-hijo-row-${idx}`}
                        className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-slate-100 py-2 text-sm">
                        <span className="flex flex-col min-w-0">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className={`truncate ${h.se_realiza ? "text-slate-700" : "text-slate-400 line-through"}`}
                              title={h.nombre}>{h.nombre}</span>
                            <GeneraBadge genera={h.se_realiza && h.accion !== "revisar" ? h.genera : null}
                              testId={`trabajo-hijo-genera-${idx}`} />
                          </span>
                          {!h.se_realiza && h.ultimo && (
                            <span className="text-[11px] text-slate-400" data-testid={`trabajo-hijo-historial-${idx}`}>
                              Hecho en trabajo {numTrabajo(h.ultimo.numero)} · {fmtMiles(h.ultimo.km)} km
                              {h.proximo_km != null ? ` · próximo ${fmtMiles(h.proximo_km)} km` : ""}
                            </span>
                          )}
                        </span>
                        <BadgeAccion accion={h.accion} testId={`trabajo-hijo-accion-${idx}`} />
                        <Switch data-testid={`trabajo-hijo-switch-${idx}`} checked={h.se_realiza}
                          onCheckedChange={(v) => setModalHijos((prev) =>
                            prev.map((x, j) => (j === idx ? { ...x, se_realiza: !!v } : x)))} />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <DialogFooter className="shrink-0 gap-2">
            <Button variant="outline" data-testid="trabajo-hijos-cerrar-btn" onClick={() => cerrarVentanaActual()}>
              Cerrar
            </Button>
            <Button variant="outline" data-testid="trabajo-hijos-guardar-btn" onClick={guardarModalHijos}
              className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 px-6">
              Guardar
            </Button>
            <Button data-testid="trabajo-hijos-siguiente-btn" onClick={irAPaso2}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
              Siguiente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RepuestoPickerDialog key={pickerKeyActivo} open={!!pickerKeyActivo} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}
        modeloId={vehiculo?.modelo_id} modeloNombre={vehiculo?.modelo?.nombre_mostrar || vehiculo?.marca || ""}
        tareaNombre={servicioPicker?.nombre} seleccion={tareaPicker?.repuestos || []}
        onConfirmar={confirmarPicker} />

      <PlantillaRepuestosDialog key={plantillaKeyActivo} open={!!plantillaKeyActivo} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}
        servicioNombre={servicioPlantilla?.nombre} grupos={tareaPlantilla?.grupos || []} sinGrupoTarifa={!!tareaPlantilla?.sinGrupoTarifa}
        onConfirmar={confirmarPlantilla} onAtras={tareaPlantilla?.hijos ? volverAPaso1 : undefined} />

      <Dialog open={!!cronoPickerActivo} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}>
        <DialogContent data-testid="crono-picker-dialog">
          <DialogHeader>
            <DialogTitle>Elegí el cronograma de mantenimiento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Hay varios cronogramas para {cronoPickerActivo?.modeloNombre || "este modelo"}. Elegí cuál aplicar:
          </p>
          <div className="space-y-2 py-2">
            {(cronoPickerActivo?.opciones || []).map((o) => (
              <button key={o.id} type="button" data-testid={`crono-picker-opcion-${o.id}`}
                onClick={() => resolverCronoElegido(o.id)}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-teal-50 hover:border-teal-300">
                {o.nombre}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cuponActivo != null} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}>
        <DialogContent data-testid="cupon-mantenimiento-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Este trabajo es un cupón de mantenimiento?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Cupones de {cuponActivo?.data?.grupo || "este grupo de cilindrada"}:
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(cuponActivo?.data?.cupones || []).map((c) => {
              const incompleto = !!c.canjeado?.incompleto;
              const bloqueado = !!c.canjeado && !incompleto;
              return (
              <label key={c.servicio_id} data-testid={`cupon-item-${c.servicio_id}`}
                className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm ${
                  bloqueado ? "opacity-60 cursor-not-allowed border-slate-100" : "cursor-pointer border-slate-200 hover:border-indigo-300"}`}>
                <input type="radio" name="cupon-sel" className="mt-1" disabled={bloqueado}
                  data-testid={`cupon-radio-${c.servicio_id}`}
                  checked={cuponSel === c.servicio_id} onChange={() => setCuponSel(c.servicio_id)} />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <b className="text-slate-700">{c.nombre}</b>
                    <span className="text-slate-400">({fmtMiles(c.km)} km)</span>
                    {!bloqueado && c.servicio_id === cuponActivo?.data?.sugerido_servicio_id && (
                      <span className="rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-1.5 py-0.5">
                        Sugerido
                      </span>
                    )}
                  </span>
                  {bloqueado && (
                    <span className="block text-xs text-slate-500 mt-0.5" data-testid={`cupon-canjeado-${c.servicio_id}`}>
                      Canjeado en el trabajo #{numTrabajo(c.canjeado.trabajo_numero)} ({fmtFecha(c.canjeado.fecha)}, {fmtMiles(c.canjeado.km)} km)
                    </span>
                  )}
                  {incompleto && (
                    <span className="block text-xs text-amber-600 mt-0.5" data-testid={`cupon-incompleto-${c.servicio_id}`}>
                      Incompleto en el trabajo #{numTrabajo(c.canjeado.trabajo_numero)} ({fmtFecha(c.canjeado.fecha)}, {fmtMiles(c.canjeado.km)} km) — completar cupón
                      {c.canjeado.pendientes?.length > 0 && (
                        <span className="block">Pendiente: {c.canjeado.pendientes.join(", ")}</span>
                      )}
                    </span>
                  )}
                </span>
              </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="cupon-no-es-btn" onClick={noEsCupon}>No es cupón</Button>
            <Button data-testid="cupon-agregar-btn" onClick={agregarCupon} disabled={!cuponSel}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Agregar cupón
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hondaOrdenEstado === "preguntar"} onOpenChange={(o) => { if (!o) hondaNo(); }}>
        <DialogContent data-testid="honda-orden-preguntar-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Desea crear la orden de cupón en Honda?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Se encolará la creación automática de la orden de cupón "{hondaTrabajoRef.current?.tareas
              ?.map((t) => t.servicio?.nombre).find(Boolean) || ""}" en el sistema de Honda.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="honda-orden-preguntar-no-btn" onClick={hondaNo} disabled={hondaEnviando}>
              No
            </Button>
            <Button data-testid="honda-orden-preguntar-si-btn" onClick={hondaSi} disabled={hondaEnviando}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Sí
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hondaOrdenEstado === "contacto"} onOpenChange={(o) => { if (!o) hondaNo(); }}>
        <DialogContent data-testid="honda-orden-contacto-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nombre de contacto</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            El cliente es una empresa y el vehículo no tiene un contacto guardado. Ingrese el nombre
            de la persona de contacto para la orden de Honda.
          </p>
          <Input data-testid="honda-orden-contacto-nombre-input" value={hondaContactoNombre}
            onChange={(e) => setHondaContactoNombre(e.target.value)} placeholder="Nombre del contacto" autoFocus />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox data-testid="honda-orden-contacto-guardar-checkbox" checked={hondaGuardarContacto}
              onCheckedChange={(v) => setHondaGuardarContacto(!!v)} />
            Guardar como contacto del vehículo
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="honda-orden-contacto-cancelar-btn" onClick={hondaNo} disabled={hondaEnviando}>
              Cancelar
            </Button>
            <Button data-testid="honda-orden-contacto-enviar-btn" onClick={hondaEnviarContacto}
              disabled={hondaEnviando || !hondaContactoNombre.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {hondaEnviando ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DatosCuponDialog open={!!cuponDatosActivo} onOpenChange={(o) => { if (!o) cerrarVentanaActual(); }}
        valorInicial={datosCupon} vehiculo={vehiculo} cuponIncompleto={!!cuponDatosActivo?.cuponIncompleto}
        onGuardar={(datos) => { setDatosCupon(datos); setErrores((e) => ({ ...e, datos_cupon: undefined })); cerrarVentanaActual(); }} />

      <GarantiaReporteClienteDialog open={!!garantiaReporteActivo}
        onOpenChange={(o) => {
          if (!o) {
            const key = garantiaReporteActivo?.key;
            actualizarTareas((prev) => prev.filter((t) => t.key !== key));
            cerrarVentanaActual();
          }
        }}
        onGuardar={(reporte) => { setGarantiaReporte(reporte); setErrores((e) => ({ ...e, garantia: undefined })); cerrarVentanaActual(); }} />

      {tienePermiso("trabajos.fotos") && (
        <Dialog open={!!fotosTrabajoId} onOpenChange={(o) => { if (!o) setFotosTrabajoId(null); }}>
          <DialogContent data-testid="fotos-ingreso-dialog" className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Fotos de ingreso</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500 -mt-2">
              Puede cerrar esta ventana sin tomar fotos y completarlas después desde "Trabajos activos".
            </p>
            <FotosTrabajo trabajoId={fotosTrabajoId} momento="ingreso" />
            <div className="flex justify-end">
              <Button data-testid="fotos-ingreso-cerrar-btn" onClick={() => setFotosTrabajoId(null)}>Cerrar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
